import React, { useState } from 'react';
import { GamificationPermissions } from '@/constants/permissions';
import { exportToCSV } from '@/utils/export';
import { useQuery } from '@tanstack/react-query';
import {
  Download,
  Filter,
  Search,
  Users,
  Wallet as WalletIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useHasPermission } from '@/hooks/use-permission';
import {
  walletService,
  type WalletQueryParams,
} from '@/services/wallet.service';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { BulkAdjustModal } from './components/bulk-adjust-modal';
import { WalletFilters } from './components/wallet-filters';

/**
 * Wallets List Page
 */
export function WalletsListPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkAdjust, setShowBulkAdjust] = useState(false);
  const [filters, setFilters] = useState<WalletQueryParams>({
    maxResultCount: 50,
    skipCount: 0,
  });

  // Check permissions
  const { hasPermission: canExport } = useHasPermission(
    GamificationPermissions.Wallet.Export,
  );
  const { hasPermission: canBulkAdjust } = useHasPermission(
    GamificationPermissions.Wallet.BulkAdjust,
  );

  // Fetch wallets
  const {
    data: walletsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin-wallets', filters, searchTerm],
    queryFn: () =>
      walletService.getUserWallets({ ...filters, search: searchTerm }),
  });

  // Handle search
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setFilters({ ...filters, skipCount: 0 });
  };

  // Handle export
  const handleExport = async () => {
    try {
      const allWallets = await walletService.getUserWallets({
        ...filters,
        search: searchTerm,
        maxResultCount: 10000,
      });
      exportToCSV(
        allWallets.items,
        [
          { key: 'userName', label: 'User Name' },
          { key: 'userEmail', label: 'Email' },
          { key: 'balance', label: 'Balance' },
          { key: 'totalEarned', label: 'Total Earned' },
          { key: 'totalSpent', label: 'Total Spent' },
          { key: 'lastTransactionDate', label: 'Last Transaction' },
          { key: 'isActive', label: 'Active' },
        ],
        'wallets',
      );
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setFilters({
      ...filters,
      skipCount: (page - 1) * (filters.maxResultCount || 50),
    });
  };

  const currentPage =
    Math.floor((filters.skipCount || 0) / (filters.maxResultCount || 50)) + 1;
  const totalPages = Math.ceil(
    (walletsData?.totalCount || 0) / (filters.maxResultCount || 50),
  );

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <p className="text-danger">Failed to load wallets</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">User Wallets</h1>
          <p className="text-muted-foreground mt-1">
            View and manage user wallets and coin balances
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canBulkAdjust && (
            <Button variant="outline" onClick={() => setShowBulkAdjust(true)}>
              <Users className="w-4 h-4 mr-2" />
              Bulk Adjust
            </Button>
          )}
          {canExport && (
            <Button onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          )}
        </div>
      </div>

      {/* Bulk Adjust Modal */}
      <BulkAdjustModal
        open={showBulkAdjust}
        onClose={() => setShowBulkAdjust(false)}
      />

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>

          {showFilters && (
            <div className="mt-4">
              <WalletFilters filters={filters} onChange={setFilters} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Wallets Table */}
      <Card>
        <CardHeader>
          <CardTitle>User Wallets ({walletsData?.totalCount || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse flex gap-4">
                  <div className="h-12 bg-muted rounded flex-1" />
                </div>
              ))}
            </div>
          ) : walletsData && walletsData.items.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead className="text-right">Balance</TableHead>
                    <TableHead className="text-right">Total Earned</TableHead>
                    <TableHead className="text-right">Total Spent</TableHead>
                    <TableHead>Last Transaction</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {walletsData.items.map((wallet) => (
                    <TableRow key={wallet.userId}>
                      <TableCell className="font-medium">
                        {wallet.userName || 'N/A'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {wallet.userEmail || 'N/A'}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-primary">
                        {(wallet.balance ?? 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-success">
                        +{(wallet.totalEarned ?? 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right text-danger">
                        -{(wallet.totalSpent ?? 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {wallet.lastTransactionDate
                          ? new Date(
                              wallet.lastTransactionDate,
                            ).toLocaleDateString()
                          : 'Never'}
                      </TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            wallet.isActive
                              ? 'bg-success-light text-success'
                              : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {wallet.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            navigate(`/gamification/wallets/${wallet.userId}`)
                          }
                        >
                          <WalletIcon className="w-4 h-4 mr-1" />
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              No wallets found
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default WalletsListPage;
